import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Building2,
  User,
  Phone,
  Mail,
  Calendar,
  FileText,
  Briefcase,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit,
} from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";

interface SubcontractorAssignment {
  _id: Id<"locationSubcontractorAssignments">;
  subcontractorId: Id<"subcontractors">;
  subcontractorName: string;
  contactName: string;
  email: string;
  phone: string;
  services: string[];
  status: "active" | "completed" | "scheduled";
  startDate?: string;
  endDate?: string;
  notes?: string;
  assignedAt: number;
}

interface ViewSubcontractorAssignmentSheetProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: SubcontractorAssignment | null;
  onEdit?: () => void;
}

export function ViewSubcontractorAssignmentSheet({
  isOpen,
  onClose,
  assignment,
  onEdit,
}: ViewSubcontractorAssignmentSheetProps) {
  // Fetch full subcontractor details
  const subcontractor = useQuery(
    api.subcontractors.getSubcontractorById,
    assignment ? { id: assignment.subcontractorId } : "skip"
  );

  if (!assignment) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case "scheduled":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "completed":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "scheduled":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString();
  };

  const formatAssignedDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="text-xl font-semibold">{assignment.subcontractorName}</SheetTitle>
          <SheetDescription className="mt-2">
            View detailed information about this subcontractor assignment
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] pr-4">
          <div className="space-y-6 py-6">
            {/* Status and Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(assignment.status)}
                <Badge variant="outline" className={`${getStatusColor(assignment.status)} border`}>
                  {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
                </Badge>
              </div>
              {onEdit && (
                <Button variant="outline" size="sm" onClick={onEdit} className="gap-2">
                  <Edit className="h-4 w-4" />
                  Edit Assignment
                </Button>
              )}
            </div>

            {/* Company Information */}
            <Card className="p-4">
              <div className="mb-4 flex items-center gap-3">
                <Building2 className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Company Information</h3>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Company:</span>
                  <span>{assignment.subcontractorName}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Contact:</span>
                  <span>{assignment.contactName}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Email:</span>
                  <span className="text-blue-600">{assignment.email}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Phone:</span>
                  <span className="text-blue-600">{assignment.phone}</span>
                </div>
                {subcontractor?.website && (
                  <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="font-medium text-gray-600">Website:</span>
                    <span className="text-blue-600">{subcontractor.website}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Services */}
            <Card className="p-4">
              <div className="mb-4 flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold">Assigned Services</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {assignment.services.map((service, index) => (
                  <Badge key={index} variant="secondary" className="text-sm">
                    {service}
                  </Badge>
                ))}
              </div>
            </Card>

            {/* Assignment Details */}
            <Card className="p-4">
              <div className="mb-4 flex items-center gap-3">
                <Calendar className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold">Assignment Details</h3>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Status:</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(assignment.status)}
                    <span className="capitalize">{assignment.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Assigned:</span>
                  <span>{formatAssignedDate(assignment.assignedAt)}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Start Date:</span>
                  <span>{formatDate(assignment.startDate)}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">End Date:</span>
                  <span>{formatDate(assignment.endDate)}</span>
                </div>
              </div>
            </Card>

            {/* Notes */}
            {assignment.notes && (
              <Card className="p-4">
                <div className="mb-4 flex items-center gap-3">
                  <FileText className="h-5 w-5 text-orange-600" />
                  <h3 className="text-lg font-semibold">Notes</h3>
                </div>
                <p className="whitespace-pre-wrap text-gray-700">{assignment.notes}</p>
              </Card>
            )}

            {/* Additional Company Details */}
            {subcontractor && (
              <Card className="p-4">
                <div className="mb-4 flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold">Company Details</h3>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="font-medium text-gray-600">Address:</span>
                    <span>
                      {subcontractor.address}, {subcontractor.city}, {subcontractor.state}{" "}
                      {subcontractor.zipCode}
                    </span>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="font-medium text-gray-600">Rating:</span>
                    <span>{subcontractor.rating}/5</span>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="font-medium text-gray-600">Status:</span>
                    <Badge
                      variant="outline"
                      className={
                        subcontractor.status === "active"
                          ? "border-green-200 bg-green-100 text-green-800"
                          : "border-gray-200 bg-gray-100 text-gray-800"
                      }
                    >
                      {subcontractor.status.charAt(0).toUpperCase() + subcontractor.status.slice(1)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] items-start gap-2">
                    <span className="font-medium text-gray-600">All Services:</span>
                    <div className="flex flex-wrap gap-1">
                      {subcontractor.services.map((service: any, index: any) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
